import { GEO_JOINT_BOUNDARIES } from '@/constants/endpoints';
import { MAP_SCALES } from '@/constants/geo';
import axios from 'axios';
import { useEffect, useState } from 'react';

export interface CityFeature {
  type: 'Feature';
  properties: { GEO_ID?: string; NAME?: string; name?: string; value?: number | null };
  geometry: unknown;
}

/**
 * Resolves City boundaries for a set of `name|state` lookup keys via the
 * joint-boundaries endpoint. GEO_ID on each returned feature is
 * `${name}, ${state}` (see ribeye-api's getJointGeoJson) — canonical's
 * geo_city_code has no relationship to the boundary table's GeoNames-keyed
 * id, so City resolves by name+state instead, the same disambiguation the
 * existing City search already relies on for same-named cities in different
 * states. Capped at 100 per request by the caller, matching the existing
 * Simplifi city-boundary fetch's own limit.
 */
export function useCityBoundaries(lookups: string[]) {
  const [features, setFeatures] = useState<CityFeature[] | null>(null);
  const [failed, setFailed] = useState(false);
  const lookupsKey = lookups.join(',');

  useEffect(() => {
    setFailed(false);
    if (lookups.length === 0) {
      setFeatures([]);
      return;
    }
    // Otherwise a new lookup set renders against the PREVIOUS one's
    // features until this request resolves — wrong boundaries matched to
    // new rows, not just a stale map.
    setFeatures(null);
    const controller = new AbortController();
    const cityLookups = lookups.map(pair => {
      const [name, state] = pair.split('|');
      return { name, state };
    });
    axios
      .post(
        GEO_JOINT_BOUNDARIES,
        { data: [{ geoType: MAP_SCALES.CITY, geoIds: [], cityLookups }] },
        { signal: controller.signal },
      )
      .then(({ data }) => {
        const featureCollection = data?.find(
          (entry: { type: string }) => entry.type === MAP_SCALES.CITY,
        )?.featureCollection;
        setFeatures((featureCollection?.features ?? []) as CityFeature[]);
      })
      .catch(error => {
        if (!axios.isCancel(error)) setFailed(true);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsKey]);

  return { features, failed };
}
